/**
 * FS-678 — Subnetwork-favicon data-layer client.
 *
 * Thin typed wrapper over `apiClient` for /api/v1/subnetwork-favicon. Keeps
 * the file free of React so the helpers are unit-testable in isolation, and
 * so the page imports only the shapes it needs.
 *
 * Validation constants here MUST stay in lock-step with
 * apps/api/src/routes/subnetwork-favicon.ts — the server is still the source
 * of truth, but the UI re-validates to avoid shipping bytes that will bounce.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubnetworkFavicon {
	url: string;
	mime: FaviconMime;
	size_bytes: number;
	updated_at: number;
}

export interface SubnetworkFaviconResponse {
	subnetwork_id: string;
	favicon: SubnetworkFavicon | null;
}

export interface SubnetworkFaviconPutResponse {
	ok: boolean;
	subnetwork_id: string;
	favicon: SubnetworkFavicon;
}

export interface SubnetworkFaviconDeleteResponse {
	ok: boolean;
	subnetwork_id: string;
	removed: boolean;
}

export const FAVICON_MIMES = [
	"image/png",
	"image/x-icon",
	"image/svg+xml",
] as const;
export type FaviconMime = (typeof FAVICON_MIMES)[number];

// Identical to MAX_BASE64_CHARS on the server.
export const MAX_BASE64_CHARS = 65_536;
// Identical to MAX_RAW_BYTES on the server.
export const MAX_RAW_BYTES = Math.floor((MAX_BASE64_CHARS * 3) / 4);

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit tests + UI disables.
// ---------------------------------------------------------------------------

export function isFaviconMime(v: unknown): v is FaviconMime {
	return (
		typeof v === "string" && (FAVICON_MIMES as readonly string[]).includes(v)
	);
}

export function isHttpsUrlForFavicon(raw: unknown): raw is string {
	if (typeof raw !== "string") return false;
	const trimmed = raw.trim();
	if (trimmed.length === 0 || trimmed.length > 2048) return false;
	try {
		const url = new URL(trimmed);
		return url.protocol === "https:";
	} catch {
		return false;
	}
}

export function inferMimeFromFilename(name: string): FaviconMime | null {
	const lower = name.toLowerCase();
	if (lower.endsWith(".png")) return "image/png";
	if (lower.endsWith(".ico")) return "image/x-icon";
	if (lower.endsWith(".svg")) return "image/svg+xml";
	return null;
}

/**
 * Reads the File as base64 (no data-url prefix) for upload. Resolves to null
 * on read errors so callers can surface a toast rather than an unhandled
 * rejection.
 */
export function fileToBase64(file: File): Promise<string | null> {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.onload = () => {
			const raw = String(reader.result ?? "");
			const idx = raw.indexOf("base64,");
			resolve(idx >= 0 ? raw.slice(idx + "base64,".length) : null);
		};
		reader.onerror = () => resolve(null);
		reader.readAsDataURL(file);
	});
}

export function formatFaviconSize(bytes: number): string {
	if (!Number.isFinite(bytes) || bytes <= 0) return "—";
	if (bytes < 1024) return `${bytes} B`;
	const kib = bytes / 1024;
	if (kib < 10) return `${kib.toFixed(1)} KiB`;
	return `${Math.round(kib)} KiB`;
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/subnetwork-favicon";

export async function fetchSubnetworkFavicon(
	subnetworkId: string,
): Promise<SubnetworkFaviconResponse> {
	return apiClient.get<SubnetworkFaviconResponse>(BASE, {
		query: { subnetwork_id: subnetworkId },
	});
}

export type PutFaviconArgs =
	| { subnetwork_id: string; url: string; mime?: FaviconMime }
	| {
			subnetwork_id: string;
			base64: string;
			mime: FaviconMime;
	  };

export async function putSubnetworkFavicon(
	args: PutFaviconArgs,
): Promise<SubnetworkFaviconPutResponse> {
	const { subnetwork_id, ...body } = args;
	return apiClient.put<SubnetworkFaviconPutResponse>(BASE, body, {
		query: { subnetwork_id },
	});
}

export async function deleteSubnetworkFavicon(
	subnetworkId: string,
): Promise<SubnetworkFaviconDeleteResponse> {
	return apiClient.delete<SubnetworkFaviconDeleteResponse>(BASE, {
		query: { subnetwork_id: subnetworkId },
	});
}

// ---------------------------------------------------------------------------
// Query keys — for TanStack Query invalidation in the page.
// ---------------------------------------------------------------------------

export const subnetworkFaviconQueryKeys = {
	all: ["subnetwork-favicon"] as const,
	one: (subnetworkId: string) =>
		[...subnetworkFaviconQueryKeys.all, subnetworkId] as const,
};
